import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Lock, LogIn, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

// Giris denemesinin neden basarisiz oldugunu ayirt eder.
// Onceden her hata "Sifre yanlis" olarak gosteriliyordu; sunucuya hic
// ulasilamadigi durumlarda bu yanlis bilgi veriyordu.
export function girisHatasiMetni(err) {
  // Sunucudan yanit hic gelmedi: ag hatasi, zaman asimi ya da servis uyaniyor.
  if (!err?.response) {
    if (err?.code === "ECONNABORTED") {
      return "Sunucu zamanında yanıt vermedi. Birkaç saniye sonra tekrar deneyin.";
    }
    return "Sunucuya ulaşılamadı. Bağlantınızı kontrol edip tekrar deneyin.";
  }

  const status = err.response.status;
  const detay = err.response.data?.detail;

  if (status === 401) return detay || "Şifre yanlış.";
  if (status === 429) return "Çok fazla deneme yapıldı. Bir dakika bekleyip tekrar deneyin.";
  if (status >= 500) return "Sunucu şu anda yanıt veremiyor. Birkaç saniye sonra tekrar deneyin.";

  return detay || "Giriş yapılamadı. Lütfen tekrar deneyin.";
}

export default function Login() {
  const { isAuthed, login } = useAuth();
  const [password, setPassword] = useState("");
  const [sifreAcik, setSifreAcik] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (isAuthed) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    try {
      await login(password.trim());
      toast.success("Giriş başarılı");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(girisHatasiMetni(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#F8F9FA] dark:bg-neutral-950">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <Logo className="h-14 w-14 shrink-0" />
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400 font-semibold leading-snug">Mobilya Toptan ve Perakende Komitesi</div>
            <div className="font-heading text-xl font-semibold leading-tight text-neutral-950 dark:text-neutral-50">Seçmen Takip</div>
          </div>
        </div>

        <Card className="p-6 border-neutral-200 dark:border-neutral-800 shadow-sm">
          <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">Sisteme Giriş</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
            Erişmek için saha ekibi şifresini girin.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.05em] font-semibold">
                Şifre
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={sifreAcik ? "text" : "password"}
                  placeholder="•••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-11 h-11 focus-visible:ring-neutral-950"
                  autoFocus
                />
                {/* Sahada telefonla girilirken yanlis yazilan sifre goze
                    carpmiyordu; gostermek hatayi anlasilir kiliyor. */}
                <button
                  type="button"
                  onClick={() => setSifreAcik((v) => !v)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                  aria-label={sifreAcik ? "Şifreyi gizle" : "Şifreyi göster"}
                  aria-pressed={sifreAcik}
                  title={sifreAcik ? "Şifreyi gizle" : "Şifreyi göster"}
                  data-testid="login-password-toggle"
                  tabIndex={-1}
                >
                  {sifreAcik ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 bg-neutral-950 hover:bg-neutral-800 text-white dark:bg-neutral-100 dark:text-neutral-950 dark:hover:bg-white transition-colors"
            >
              {loading ? "Doğrulanıyor..." : (
                <>
                  <LogIn className="h-4 w-4 mr-2" />
                  Giriş Yap
                </>
              )}
            </Button>
          </form>
        </Card>

        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-6 text-center">
          Sadece yetkili saha ekibi için • Ticaret ve Sanayi Odası
        </p>
      </div>
    </div>
  );
}
